"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { type TaskListColor, taskListColorSchema } from "@orsane/schema/task-lists";
import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useHotkeys } from "react-hotkeys-hook";
import { toast } from "sonner";
import { z } from "zod";
import EnterButton from "@/components/buttons/EnterButton";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useInsertTaskListMutation } from "@/mutations/task-list-mutations";
import { taskListRoute } from "@/routes/taskListRoute";
import useNewListStore from "@/stores/useNewListStore";
import ColorSelector from "./ColorSelector";
import { Field, FieldError } from "./ui/field";

const defaultValues = {
    name: "",
    color: "Blue" as TaskListColor,
};

const newListFormSchema = z.object({
    name: z
        .string()
        .min(1, "List name is required")
        .min(2, "List name must be at least 2 characters")
        .max(100, "List name cannot exceed 100 characters"),
    color: taskListColorSchema.refine((val) => val !== undefined, {
        message: "Color is required",
    }),
});

type NewListFormValues = z.infer<typeof newListFormSchema>;

export default function NewListDialog() {
    const { mutateAsync, isPending } = useInsertTaskListMutation();
    const { open, setOpen } = useNewListStore();
    const navigate = useNavigate();

    useHotkeys("mod+j", () => setOpen(true), { preventDefault: true });

    const form = useForm({
        resolver: zodResolver(newListFormSchema),
        defaultValues: defaultValues,
    });

    useEffect(() => {
        if (open) {
            form.reset(defaultValues);
            setTimeout(() => {
                // Seems to only work if we wait for the next tick
                form.setFocus("name");
            });
        }
    }, [open, form]);

    const handleSubmit = async (values: NewListFormValues) => {
        const res = await mutateAsync(values);

        toast.success(`List created`, {
            description: values.name,
            action: {
                label: "Open",
                onClick: () =>
                    navigate({
                        to: taskListRoute.to,
                        params: { taskListId: res.id },
                    }),
            },
        });

        form.reset();
        setOpen(false);
    };

    const handleColorApply = (color: TaskListColor) => {
        form.setValue("color", color);
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="sm:max-w-[425px] p-4">
                <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-2">
                    <div className="flex items-start">
                        <ColorSelector buttonClassName="-translate-x-2" color={form.watch("color")} onApply={handleColorApply} />

                        <div className="flex-1">
                            <Controller
                                control={form.control}
                                name="name"
                                render={({ field, fieldState }) => (
                                    <Field className="gap-1">
                                        <Input
                                            data-1p-ignore
                                            {...field}
                                            placeholder="New list name"
                                            className="col-span-4 focus:ring-0 focus-visible:ring-0 focus:outline-none focus-visible:outline-none border-none px-0 leading-none shadow-none"
                                        />
                                        {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                                    </Field>
                                )}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)} disabled={isPending}>
                            Cancel
                        </Button>
                        <EnterButton isSubmitting={isPending} disabled={!form.watch("name")}>
                            Create list
                        </EnterButton>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
